/**
 * Milo's answers as BLOCKS and INLINE RUNS, never as HTML.
 *
 * The model writes light Markdown — a bold word, a dash list, a numbered list, the
 * odd heading — and a bubble that printed the asterisks read as a bubble that had
 * not been finished ("**prosto**", 7. 9. 2026). Rendering it with a Markdown-to-HTML
 * library would hand the model a way to put an element on the page; this keeps the
 * one rule AssistantText.vue already keeps: the model's words are data, drawn by a
 * template, and a link is a link only because `linkify` said so.
 *
 * What is understood, on purpose and nothing more: paragraphs, `-`/`*`/`•` bullets,
 * `1.` numbered items, `#` headings (drawn as a bold line), `**bold**`, `*italic*` /
 * `_italic_`, and `` `code` ``. Everything else is text.
 */
import { linkify, type ILinkSegment } from '@/libs/linkify'

export type InlineKind = 'bold' | 'italic' | 'code'

export interface IInlineRun {
    /** The look; a plain run carries the link segments as they came from linkify. */
    style: InlineKind | ''
    segments: ILinkSegment[]
}

export type BlockKind = 'paragraph' | 'heading' | 'bullets' | 'numbers'

export interface IBlock {
    kind: BlockKind
    /** One run list per line for a paragraph or heading; one per item for a list. */
    items: IInlineRun[][]
}

const INLINE = /(\*\*[^*\n]+\*\*|`[^`\n]+`|(?<![\w*])\*[^*\n]+\*(?![\w*])|(?<!\w)_[^_\n]+_(?!\w))/

/** Splits one line into styled runs; the plain runs are linkified. */
export const inlineRuns = (line: string): IInlineRun[] => {
    const runs: IInlineRun[] = []

    for (const piece of line.split(INLINE)) {
        if (!piece) continue

        if (piece.startsWith('**') && piece.endsWith('**') && piece.length > 4) {
            runs.push({ style: 'bold', segments: linkify(piece.slice(2, -2)) })
        } else if (piece.startsWith('`') && piece.endsWith('`') && piece.length > 2) {
            runs.push({ style: 'code', segments: [{ kind: 'text', text: piece.slice(1, -1), url: '' }] })
        } else if ((piece.startsWith('*') && piece.endsWith('*') || piece.startsWith('_') && piece.endsWith('_')) && piece.length > 2) {
            runs.push({ style: 'italic', segments: linkify(piece.slice(1, -1)) })
        } else {
            runs.push({ style: '', segments: linkify(piece) })
        }
    }

    return runs
}

const BULLET  = /^\s*[-*•]\s+(.*)$/
const NUMBER  = /^\s*\d+[.)]\s+(.*)$/
const HEADING = /^\s*#{1,6}\s+(.*)$/

/** Turns the model's text into blocks. Blank lines separate paragraphs. */
export const parseMarkdown = (text: string): IBlock[] => {
    const blocks: IBlock[] = []
    let paragraph: IInlineRun[][] = []

    const flush = () => {
        if (paragraph.length) blocks.push({ kind: 'paragraph', items: paragraph })
        paragraph = []
    }

    for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
        const line = raw.trimEnd()

        if (!line.trim()) {
            flush()
            continue
        }

        const heading = line.match(HEADING)
        if (heading) {
            flush()
            blocks.push({ kind: 'heading', items: [inlineRuns(heading[1])] })
            continue
        }

        const bullet = line.match(BULLET)
        const number = bullet ? null : line.match(NUMBER)
        if (bullet || number) {
            flush()
            const kind: BlockKind = bullet ? 'bullets' : 'numbers'
            const last = blocks[blocks.length - 1]
            const item = inlineRuns((bullet ?? number)![1])
            if (last && last.kind === kind) last.items.push(item)
            else blocks.push({ kind, items: [item] })
            continue
        }

        paragraph.push(inlineRuns(line))
    }

    flush()

    return blocks
}
